import {useMutation, useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/RegistrationsService';
import {useState} from 'react';

export const useRegistrations = () => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const registrationsQuery = useInfiniteQuery({
        queryKey: ['registrations', keyword, limit],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
        }
    });

    const fetchRecordsAction = () => {
        if (registrationsQuery.hasNextPage) {
            registrationsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids: string[]) => {
        if (ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = registrationsQuery.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: registrationsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: registrationsQuery.hasNextPage ? 'has-more' : null,
        isFetchingMore: registrationsQuery.isFetchingNextPage,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
